import React, { useEffect, useRef, useState } from 'react';
import { Building2, CalendarClock } from 'lucide-react';
import {
  Alert, Button, ErrorState, FilterChip, SegmentedControl, Select, SkeletonForm, Toggle,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useAppNavigate } from '../../../hooks/useAppNavigate';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import { useBookingOptions } from '../useDoctorPage';
import {
  BOOKING_LIMITS, BOOKING_MODES, bookingClinics, bookingDefaultServices, bookingDraft,
  bookingFieldOf, bookingHorizonOptions, bookingInvalid, bookingMissingServices, bookingPayload,
  bookingSaved, bookingServicesFor, bookingStored, sameDraft, saveStatus, splitBookingFieldErrors,
} from '../MyPage.shared';
import { SaveRow, useFieldStyle } from '../parts/common';

// ─────────────────────────────────────────────────────────────────────────────
// «نوبت‌دهی» — the booking section of the doctor's page (PM-0468 §4.5,
// docs/SERVICE_OFFERING_AND_BOOKING_DESIGN.md). Archetype D (settings card):
// the enable switch, the clinic (one membership = preselected and fixed), the
// mode, the service chips the clinic made public, the horizon. Save is the
// card's SaveRow through the page's section update; the four booking codes a
// save or a publish may answer with land on their field as a red helper line.
//
// The live slot strip belongs to the published page (the landing owns it); the
// editor says so under the fields rather than imitating it.
// ─────────────────────────────────────────────────────────────────────────────

const TESTID = 'mypage-booking';
const fieldLabel = { fontSize: '0.8125rem', fontWeight: 600, color: C.textPrimary };
const hint = { fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.6 };
const redLine = { fontSize: '0.75rem', color: C.red, lineHeight: 1.6 };
const group = { display: 'grid', gap: '0.5rem' };

/** The one line under a field: the server's sentence when it wrote one, else this card's text. */
function FieldError({ errors, testId }) {
  const s = useMyPageStrings();
  if (!errors?.length) return null;
  return (
    <div role="alert" data-testid={testId} style={redLine}>
      {errors.map((e, i) => (
        <div key={`${e.code}-${i}`} dir="auto">{e.message || s(`myPage.sections.booking.errors.${e.code}`)}</div>
      ))}
    </div>
  );
}

/**
 * The service chips: ONE tab stop, arrows (and Home/End) move inside it in the
 * direction the doctor sees; each chip is a toggle button with `aria-pressed`.
 */
function ServiceChips({ services, chosen, missing, disabled, onToggle }) {
  const s = useMyPageStrings();
  const { formatNumber, isRTL } = useTranslation();
  const ref = useRef(null);
  const [focusIdx, setFocusIdx] = useState(0);

  // Chips the group can land on: the clinic's current list, then the vanished ones.
  const rows = [
    ...services.map((svc) => ({ id: String(svc.catalogId), svc, missing: false })),
    ...missing.map((id) => ({ id, svc: null, missing: true })),
  ];
  useEffect(() => { if (focusIdx >= rows.length) setFocusIdx(Math.max(0, rows.length - 1)); }, [rows.length, focusIdx]);

  const atMax = chosen.length >= BOOKING_LIMITS.servicesMax;

  const focusAt = (idx) => {
    const next = (idx + rows.length) % rows.length;
    setFocusIdx(next);
    const el = ref.current?.querySelectorAll('[data-chip]')[next];
    el?.focus();
  };
  const onKeyDown = (e, idx) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) || !rows.length) return;
    e.preventDefault();
    if (e.key === 'Home') return focusAt(0);
    if (e.key === 'End') return focusAt(rows.length - 1);
    const forward = isRTL ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
    return focusAt(idx + (forward ? 1 : -1));
  };

  return (
    <div
      ref={ref}
      role="group"
      aria-label={s('myPage.sections.booking.services')}
      data-testid={`${TESTID}-services`}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
    >
      {rows.map((row, i) => {
        const active = chosen.includes(row.id);
        const kindKey = row.svc?.patientBookable ? 'direct' : 'referral';
        return (
          <FilterChip
            key={row.id}
            data-chip
            data-testid={`${TESTID}-service-${row.id}`}
            data-missing={row.missing || undefined}
            active={active}
            tabIndex={i === focusIdx ? 0 : -1}
            disabled={disabled || (!active && atMax)}
            onClick={() => { setFocusIdx(i); onToggle(row.id); }}
            onKeyDown={(e) => onKeyDown(e, i)}
            onFocus={() => setFocusIdx(i)}
            title={row.missing ? s('myPage.sections.booking.unavailable') : undefined}
            style={row.missing ? { textDecoration: 'line-through', color: C.textMuted } : undefined}
            className="h-auto min-h-11 py-1.5 whitespace-normal"
          >
            <span dir="auto">{row.svc?.name || row.id}</span>
            {row.svc && row.svc.durationMinutes > 0 ? (
              <span style={{ fontSize: '0.6875rem', opacity: 0.8 }}>
                · {s('myPage.sections.booking.duration', { minutes: formatNumber(row.svc.durationMinutes) })}
              </span>
            ) : null}
            <span
              data-kind={row.missing ? 'missing' : kindKey}
              style={{
                fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.375rem', borderRadius: RADIUS.pill,
                background: row.missing ? C.bg : kindKey === 'direct' ? 'rgba(0,190,178,0.14)' : 'rgba(245,158,11,0.14)',
                color: row.missing ? C.textMuted : kindKey === 'direct' ? C.teal : C.amber,
              }}
            >
              {row.missing ? s('myPage.sections.booking.unavailable') : s(`myPage.sections.booking.kinds.${kindKey}`)}
            </span>
          </FilterChip>
        );
      })}
    </div>
  );
}

export default function BookingEditor({ section, actions, cards, readOnly, fieldErrors = [] }) {
  const s = useMyPageStrings();
  const { formatNumber } = useTranslation();
  const fieldStyle = useFieldStyle();
  const navigate = useAppNavigate();
  const { options, loading, error, retry } = useBookingOptions();
  // The baseline is what is STORED; on a never-saved section the defaults (the
  // sole clinic, its directly bookable services) are proposed on top of it, so
  // they read as a change the doctor saves rather than as already saved.
  const { draft, setDraft, dirty, adopt, reset } = useDraft(bookingStored(section));
  const proposed = options && !bookingSaved(section) ? bookingDraft(section, options) : null;
  const proposedKey = JSON.stringify(proposed);
  useEffect(() => {
    if (!proposed || dirty) return;
    setDraft((d) => (sameDraft(d, proposed) ? d : proposed));
    // `proposedKey` stands for `proposed` by value; re-proposing on every keystroke would fight the doctor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedKey]);
  const card = cards.booking || {};
  const toggleCard = cards['toggle-booking'] || {};

  const clinics = bookingClinics(options);
  const services = bookingServicesFor(options, draft.clinicId);
  const missing = bookingMissingServices(draft, options);
  const payload = bookingPayload(draft, options);

  // A refused SAVE with one of the four codes is shown on its field, like a
  // publish refusal; anything else stays the SaveRow's inline alert.
  const saveCode = card.error?.extensions?.code;
  const saveField = bookingFieldOf(saveCode);
  const { fields } = splitBookingFieldErrors([
    ...(saveField ? [{ code: saveCode, message: card.error?.message }] : []),
    ...fieldErrors,
  ]);

  const save = async () => {
    const res = await actions.updateSection('booking', 'booking', { content: payload });
    const saved = res.ok ? res.page?.sections?.find((x) => x.kind === 'booking') : null;
    if (saved) adopt(bookingDraft(saved, options));
  };

  const setEnabled = (enabled) => {
    // Switching on a never-saved section sends the pre-checked defaults in the
    // same write, so «انتشار» does not immediately refuse an empty list.
    const input = { enabled };
    if (enabled && !bookingSaved(section) && payload.clinic_id && payload.services.length) input.content = payload;
    return actions.updateSection('toggle-booking', 'booking', input);
  };

  const chooseClinic = (clinicId) => setDraft((d) => ({
    ...d, clinicId, services: options ? bookingDefaultServices(options, clinicId) : [],
  }));
  const toggleService = (id) => setDraft((d) => ({
    ...d, services: d.services.includes(id) ? d.services.filter((x) => x !== id) : [...d.services, id],
  }));

  const state = loading ? 'loading' : error ? 'error' : clinics.length === 0 ? 'empty' : 'content';
  const disabled = readOnly || state !== 'content';

  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid={TESTID}>
      <div style={hint}>{s('myPage.sections.booking.intro')}</div>

      <Toggle
        checked={!!section.enabled}
        onChange={setEnabled}
        disabled={readOnly || !!toggleCard.saving}
        label={s('myPage.sections.booking.enabled')}
        description={s('myPage.sections.booking.enabledHint')}
        data-testid={`${TESTID}-enabled`}
      />

      <div data-testid={`${TESTID}-options`} data-state={state} style={{ display: 'grid', gap: '1rem' }}>
        {state === 'loading' ? (
          <SkeletonForm fields={3} actions={0} label={s('myPage.sections.booking.services')} />
        ) : null}

        {state === 'error' ? (
          <ErrorState
            title={s('myPage.sections.booking.loadFailed')}
            onRetry={retry}
            style={{ padding: '1.5rem 1rem' }}
            data-testid={`${TESTID}-options-error`}
          />
        ) : null}

        {state === 'empty' ? (
          <Alert
            variant="warning"
            icon={<Building2 size={16} aria-hidden />}
            title={s('myPage.sections.booking.noClinicsTitle')}
            data-testid={`${TESTID}-no-clinics`}
            action={(
              <Button
                size="sm" variant="outline"
                onClick={() => navigate('profile', { tab: 'clinics' })}
                data-testid={`${TESTID}-go-clinics`}
              >
                {s('myPage.sections.booking.goClinics')}
              </Button>
            )}
          >
            {s('myPage.sections.booking.noClinicsBody')}
          </Alert>
        ) : null}

        {state === 'content' ? (
          <>
            {clinics.length === 1 ? (
              <div style={group}>
                <div style={fieldLabel}>{s('myPage.sections.booking.clinic')}</div>
                <div
                  data-testid={`${TESTID}-clinic-fixed`}
                  data-clinic-id={clinics[0].id}
                  dir="auto"
                  style={{ fontSize: '0.875rem', color: C.textPrimary, display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <Building2 size={16} aria-hidden style={{ color: C.textSecondary, flexShrink: 0 }} />
                  <span>{clinics[0].name || clinics[0].id}{clinics[0].city ? ` · ${clinics[0].city}` : ''}</span>
                </div>
                <div style={hint}>{s('myPage.sections.booking.clinicSingleHint')}</div>
                <FieldError errors={fields.clinic} testId={`${TESTID}-clinic-error`} />
              </div>
            ) : (
              <div style={group}>
                <Select
                  label={s('myPage.sections.booking.clinic')}
                  placeholder={s('myPage.sections.booking.clinicPlaceholder')}
                  value={draft.clinicId}
                  onChange={(e) => chooseClinic(e.target.value)}
                  options={clinics.map((c) => ({ value: String(c.id), label: c.city ? `${c.name || c.id} · ${c.city}` : (c.name || c.id) }))}
                  disabled={readOnly}
                  aria-invalid={fields.clinic.length ? true : undefined}
                  style={fieldStyle}
                  data-testid={`${TESTID}-clinic`}
                />
                <FieldError errors={fields.clinic} testId={`${TESTID}-clinic-error`} />
              </div>
            )}

            <div style={group}>
              <div style={fieldLabel}>{s('myPage.sections.booking.mode')}</div>
              <SegmentedControl
                semantics="filter"
                size="lg"
                ariaLabel={s('myPage.sections.booking.mode')}
                value={draft.mode}
                onChange={(mode) => { if (!readOnly) setDraft((d) => ({ ...d, mode })); }}
                options={BOOKING_MODES.map((m) => ({ value: m, label: s(`myPage.sections.booking.modes.${m}`), disabled: readOnly }))}
                data-testid={`${TESTID}-mode`}
                style={{ maxWidth: '100%', flexWrap: 'wrap' }}
              />
              <div style={hint}>{s(`myPage.sections.booking.modeHints.${draft.mode}`)}</div>
            </div>

            {draft.clinicId ? (
              <div style={group}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ ...fieldLabel, flex: 1 }}>{s('myPage.sections.booking.services')}</span>
                  <span style={{ fontSize: '0.6875rem', color: C.textMuted, fontVariantNumeric: 'tabular-nums' }} data-testid={`${TESTID}-services-count`}>
                    {s('myPage.items.count', { count: formatNumber(draft.services.length), max: formatNumber(BOOKING_LIMITS.servicesMax) })}
                  </span>
                </div>
                {services.length || missing.length ? (
                  <ServiceChips
                    services={services}
                    chosen={draft.services}
                    missing={missing}
                    disabled={readOnly}
                    onToggle={toggleService}
                  />
                ) : (
                  <div style={hint} data-testid={`${TESTID}-services-empty`}>{s('myPage.sections.booking.servicesEmpty')}</div>
                )}
                <div style={hint}>{s('myPage.sections.booking.servicesHint')}</div>
                {missing.length ? <div style={{ ...hint, color: C.amber }}>{s('myPage.sections.booking.unavailableHint')}</div> : null}
                <FieldError errors={fields.services} testId={`${TESTID}-services-error`} />
              </div>
            ) : null}

            <Select
              label={s('myPage.sections.booking.horizon')}
              helperText={s('myPage.sections.booking.horizonHint')}
              placeholder=""
              value={String(draft.horizonDays)}
              onChange={(e) => setDraft((d) => ({ ...d, horizonDays: Number(e.target.value) }))}
              options={bookingHorizonOptions().map((n) => ({ value: String(n), label: s('myPage.sections.booking.horizonDays', { days: formatNumber(n) }) }))}
              disabled={readOnly}
              style={fieldStyle}
              data-testid={`${TESTID}-horizon`}
            />
          </>
        ) : null}
      </div>

      <Alert variant="neutral" icon={<CalendarClock size={16} aria-hidden />} data-testid={`${TESTID}-preview-note`}>
        {s('myPage.sections.booking.previewNote')}
      </Alert>

      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={saveField ? null : card.error}
        onSave={save}
        onReset={reset}
        disabled={disabled || bookingInvalid(draft)}
        testId={TESTID}
      />
    </div>
  );
}

// The card carries its own enable switch, so SectionEditor's «پنهان» notice
// would be a second control for the same fact.
BookingEditor.ownsEnable = true;
